import { type JSX, Suspense, lazy } from "react";

import { assertNever, scalar } from "@/lib/report-format";
import type {
  EvidenceBinding,
  ReportBlock as ReportBlockValue,
  ReportImageBlobFetcher,
  ReportImageRelation,
  ReportRevisionSource,
} from "@/lib/reports";
import { reportImageFetcherNotWired } from "@/lib/reports";

import { ReportDiagram } from "./ReportDiagram";
import { ReportImageBlockView } from "./ReportImage";
import { ReportMarkdown } from "./ReportMarkdown";

interface ReportBlockProps {
  block: ReportBlockValue;
  sources: ReportRevisionSource[];
  imageRelations?: ReportImageRelation[];
  fetchImageBlob?: ReportImageBlobFetcher;
}

// recharts is heavy and only needed when a report contains a chart block, so it
// is code-split out of the entry chunk.
const ReportChart = lazy(() =>
  import("./ReportChart").then((module) => ({ default: module.ReportChart })),
);

const EMPTY_IMAGE_RELATIONS: ReportImageRelation[] = [];

function Evidence({
  evidence,
}: { evidence: EvidenceBinding | null | undefined }): JSX.Element | null {
  if (!evidence) return null;
  return (
    <span className="text-xs text-[var(--report-muted-fg)]">
      {evidence.support === "inference" ? "Inferencia" : "Evidencia"}:{" "}
      {evidence.source_handles.join(", ")}
    </span>
  );
}

export function ReportBlock({
  block,
  sources,
  imageRelations = EMPTY_IMAGE_RELATIONS,
  fetchImageBlob = reportImageFetcherNotWired,
}: ReportBlockProps): JSX.Element {
  let content: JSX.Element;
  switch (block.kind) {
    case "executive_summary":
      content = (
        <div className="space-y-4">
          <p className="text-xl font-medium leading-8">{block.thesis.text}</p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {block.highlights.map((highlight) => (
              <li
                className="rounded-lg border border-[var(--report-border)] bg-[var(--report-muted)] p-4 leading-6"
                key={highlight.text}
              >
                {highlight.text}
              </li>
            ))}
          </ul>
        </div>
      );
      break;
    case "kpi_grid":
      content = (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {block.items.map((item) => (
            <div
              className="rounded-xl border border-[var(--report-border)] p-4"
              key={item.metric_id}
            >
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--report-muted-fg)]">
                {item.label}
              </p>
              <p className="mt-2 text-2xl font-semibold tabular-nums">
                {scalar(item.value)} {item.unit ?? ""}
              </p>
              {item.delta !== undefined || item.period ? (
                <p className="mt-1 text-sm text-[var(--report-muted-fg)]">
                  {item.delta !== undefined ? scalar(item.delta) : ""} {item.period ?? ""}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      );
      break;
    case "chart":
      content = (
        <Suspense fallback={<p>Cargando…</p>}>
          <ReportChart block={block} />
        </Suspense>
      );
      break;
    case "diagram":
      content = <ReportDiagram block={block} />;
      break;
    case "table":
      content = (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-lg border border-[var(--report-border)]">
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-[var(--report-muted)]">
                <tr>
                  {block.columns.map((column) => (
                    <th className="px-3 py-2 font-semibold" key={column.column_id}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row) => (
                  <tr className="border-t border-[var(--report-border)]" key={row.row_id}>
                    {block.columns.map((column) => (
                      <td className="px-3 py-2" key={column.column_id}>
                        {scalar(row.cells[column.column_id])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.takeaway ? <p className="leading-7">{block.takeaway.text}</p> : null}
        </div>
      );
      break;
    case "narrative":
    case "appendix":
      content = (
        <div className="space-y-4">
          {block.segments.map((segment) => (
            <ReportMarkdown
              key={segment.segment_id}
              markdown={segment.markdown}
              sources={sources}
            />
          ))}
        </div>
      );
      break;
    case "callout":
      content = (
        <div
          className="rounded-xl border-l-4 bg-[var(--report-accent-soft)] p-5 leading-7"
          style={{
            borderLeftColor:
              block.intent === "risk" ? "var(--report-risk)" : "var(--report-accent)",
          }}
        >
          {block.content.text}
        </div>
      );
      break;
    case "recommendations":
      content = (
        <ol className="space-y-3">
          {block.items.map((item, index) => (
            <li
              className="flex gap-3 rounded-xl border border-[var(--report-border)] p-4"
              key={item.item_id}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--report-accent)] text-sm font-semibold text-white">
                {index + 1}
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-semibold">{item.action}</p>
                  <span className="rounded-full bg-[var(--report-muted)] px-2 py-0.5 text-xs uppercase">
                    {item.priority}
                  </span>
                </div>
                <p className="mt-1 leading-6 text-[var(--report-muted-fg)]">{item.rationale}</p>
                {item.owner || item.horizon ? (
                  <p className="mt-2 text-xs text-[var(--report-muted-fg)]">
                    {[item.owner, item.horizon].filter(Boolean).join(" · ")}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      );
      break;
    case "image":
      content = (
        <ReportImageBlockView
          block={block}
          relations={imageRelations}
          fetchImageBlob={fetchImageBlob}
        />
      );
      break;
    default:
      return assertNever(block);
  }

  const evidence = "evidence" in block ? block.evidence : undefined;
  return (
    <section
      className="scroll-mt-24 border-t border-[var(--report-border)] py-7 first:border-t-0"
      id={`report-section-${block.block_id}`}
    >
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-xl font-semibold tracking-tight">{block.heading}</h2>
        <Evidence evidence={evidence} />
      </div>
      {content}
    </section>
  );
}
